/* eslint-disable consistent-return */
/* eslint-disable @typescript-eslint/consistent-return */
import type React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type {
	ConnectionState,
	Conversation,
	Message
} from '@twilio/conversations'
import { Client as ConversationsClient } from '@twilio/conversations'
import { useGetChatToken } from 'lib/services/chat.service'
import { ChatContext } from 'lib/contexts/chatContext'

const parseConversationDisplayName = (
	conversationFriendlyName: string
): { rfpName: string; venueName: string } | string => {
	if (conversationFriendlyName) {
		const parts = conversationFriendlyName.split('|')
		return {
			rfpName: parts[0],
			venueName: parts.length > 1 ? parts[1] : 'Planner Internal Chat'
		}
	}
	return 'New RFP'
}

const parseConversationUniqueName = (conversationUniqueName: string | null) => {
	const parts = conversationUniqueName?.split('|')
	return {
		rfpId: parts?.[0] ?? '',
		venueId: (parts?.length ?? 0) > 1 ? (parts?.[1] ?? '') : ''
	}
}

const ChatProvider = ({
	userProfileId,
	children
}: {
	userProfileId: string
	children: React.ReactNode
}) => {
	const [chatClient, setChatClient] = useState<ConversationsClient | null>(null)
	const [chatClientInitialized, setChatClientInitialized] = useState(false)
	const [conversations, setConversations] = useState<Conversation[]>([])
	const [unreadConversations, setUnreadConversations] = useState<
		(
			| Conversation
			| {
					unreadMessagesCount: number
					lastMessage: Message[] | undefined
					uniqueName: string
					friendlyName: string
			  }
		)[]
	>([])
	const [accessedConversations, setAccessedConversations] = useState<
		Set<string>
	>(new Set())

	const { refetch: getChatToken } = useGetChatToken(userProfileId, false)

	// Refresh token and update the chat client if necessary
	const refetchChatToken = useCallback(async () => {
		try {
			const response = await getChatToken()
			if (response.data) await chatClient?.updateToken(response.data)
		} catch (error) {
			console.error('Failed to update chat token', error)
		}
	}, [getChatToken, chatClient])

	const updateUnreadConversation = useCallback(
		async (conversation: Conversation, forceUpdate = false) => {
			try {
				const messages = await conversation.getMessages(50)
				const messagesFromOthers = messages.items.filter(
					m => m.author !== userProfileId
				)

				if (messagesFromOthers.length === 0) {
					setUnreadConversations(previous =>
						previous.filter(c => c.uniqueName !== conversation.uniqueName)
					)
					return
				}

				const isFirstAccess = !accessedConversations.has(
					conversation.uniqueName ?? ''
				)
				let unreadMessages: Message[] = []

				if (isFirstAccess || forceUpdate) {
					const lastConsumedIndex = conversation.lastReadMessageIndex

					if (lastConsumedIndex !== null && lastConsumedIndex !== undefined) {
						unreadMessages = messagesFromOthers.filter(
							message => message.index > lastConsumedIndex
						)
					} else {
						unreadMessages = messagesFromOthers
					}
				} else {
					const count = await conversation.getUnreadMessagesCount()
					if (count && count > 0) {
						const twilioUnreadMessages = await conversation.getMessages(
							Math.min(count, 99)
						)
						unreadMessages = twilioUnreadMessages.items.filter(
							m => m.author !== userProfileId
						)
					}
				}

				if (unreadMessages.length > 0) {
					setUnreadConversations(previous => {
						const filtered = previous.filter(
							c => c.uniqueName !== conversation.uniqueName
						)
						const updated = [
							...filtered,
							{
								uniqueName: conversation.uniqueName,
								friendlyName: conversation.friendlyName,
								unreadMessagesCount: unreadMessages.length,
								lastMessage: unreadMessages
							} as unknown as Conversation
						]
						return updated.sort((a, b) => {
							const aLastMessage =
								'lastMessage' in a && Array.isArray(a.lastMessage)
									? a.lastMessage[a.lastMessage.length - 1]?.dateCreated
									: new Date(0)
							const bLastMessage =
								'lastMessage' in b && Array.isArray(b.lastMessage)
									? b.lastMessage[b.lastMessage.length - 1]?.dateCreated
									: new Date(0)

							if (aLastMessage && bLastMessage) {
								return bLastMessage.getTime() - aLastMessage.getTime()
							}
							return 0
						})
					})
				} else {
					setUnreadConversations(previous =>
						previous.filter(c => c.uniqueName !== conversation.uniqueName)
					)
				}
			} catch (error) {
				console.error('Failed to update unread conversation', error)
			}
		},
		[userProfileId, accessedConversations]
	)

	const markConversationAccessed = useCallback(
		(conversationUniqueName: string) => {
			setAccessedConversations(
				prev => new Set([...prev, conversationUniqueName])
			)
		},
		[]
	)

	const loadInitialConversations = useCallback(
		async (c: ConversationsClient | null) => {
			try {
				const conversationsPaginator = await c?.getSubscribedConversations()
				if (conversationsPaginator) {
					const conversationsList = conversationsPaginator.items
					setConversations(conversationsList)

					await Promise.all(
						conversationsList.map(async conversation => {
							await updateUnreadConversation(conversation)
						})
					)
				}
			} catch (error) {
				console.error('Failed to load initial conversations', error)
			}
		},
		[updateUnreadConversation]
	)

	useEffect(() => {
		if (chatClientInitialized) {
			const conversationHandlers = {
				messageAdded: (message: Message) => {
					if (message.conversation.uniqueName) {
						globalThis.setTimeout(async () => {
							await updateUnreadConversation(message.conversation, true)
						}, 1500)
					}
				},
				conversationAdded: async (conversation: Conversation) => {
					setConversations(previous => [...previous, conversation])
					await updateUnreadConversation(conversation, false)
				},
				conversationUpdated: async ({
					conversation
				}: {
					conversation: Conversation
				}) => {
					await updateUnreadConversation(conversation, true)
				},
				conversationJoined: async (conversation: Conversation) => {
					setConversations(previous => [...previous, conversation])
					await updateUnreadConversation(conversation, false)
				},
				conversationLeft: (conversation: Conversation) => {
					setConversations(previous =>
						previous.filter(c => c.uniqueName !== conversation.uniqueName)
					)
					setUnreadConversations(previous =>
						previous.filter(c => c.uniqueName !== conversation.uniqueName)
					)
					setAccessedConversations(prev => {
						const newSet = new Set(prev)
						newSet.delete(conversation.uniqueName ?? '')
						return newSet
					})
				},
				connectionStateChanged: async (connectionState: ConnectionState) => {
					if (connectionState === 'disconnected') await refetchChatToken()
				},
				tokenAboutToExpire: refetchChatToken,
				tokenExpired: refetchChatToken
			}

			chatClient?.on('messageAdded', conversationHandlers.messageAdded)
			chatClient?.on(
				'conversationAdded',
				conversationHandlers.conversationAdded
			)
			chatClient?.on(
				'conversationUpdated',
				conversationHandlers.conversationUpdated
			)
			chatClient?.on(
				'conversationJoined',
				conversationHandlers.conversationJoined
			)
			chatClient?.on('conversationLeft', conversationHandlers.conversationLeft)
			chatClient?.on(
				'connectionStateChanged',
				conversationHandlers.connectionStateChanged
			)
			chatClient?.on(
				'tokenAboutToExpire',
				conversationHandlers.tokenAboutToExpire
			)
			chatClient?.on('tokenExpired', conversationHandlers.tokenExpired)

			return () => {
				chatClient?.off('messageAdded', conversationHandlers.messageAdded)
				chatClient?.off(
					'conversationAdded',
					conversationHandlers.conversationAdded
				)
				chatClient?.off(
					'conversationUpdated',
					conversationHandlers.conversationUpdated
				)
				chatClient?.off(
					'conversationJoined',
					conversationHandlers.conversationJoined
				)
				chatClient?.off(
					'conversationLeft',
					conversationHandlers.conversationLeft
				)
				chatClient?.off(
					'connectionStateChanged',
					conversationHandlers.connectionStateChanged
				)
				chatClient?.off(
					'tokenAboutToExpire',
					conversationHandlers.tokenAboutToExpire
				)
				chatClient?.off('tokenExpired', conversationHandlers.tokenExpired)
			}
		}
	}, [
		chatClientInitialized,
		chatClient,
		refetchChatToken,
		updateUnreadConversation
	])

	useEffect(() => {
		if (userProfileId && !chatClientInitialized) {
			getChatToken()
				.then(r => {
					if (r.data) {
						try {
							const client = new ConversationsClient(r.data, {
								logLevel: 'info'
							})
							setChatClient(client)
							setChatClientInitialized(true)
							client.on('initialized', async () => {
								await loadInitialConversations(client)
							})
						} catch (error: unknown) {
							console.log(
								// eslint-disable-next-line @typescript-eslint/restrict-template-expressions
								`chat client initialization failed: ${error}`
							)
						}
					}
				})
				.catch((error: unknown) =>
					// eslint-disable-next-line @typescript-eslint/restrict-template-expressions
					console.log(`chat client token failed: ${error}`)
				)
		}
	}, [
		userProfileId,
		chatClientInitialized,
		getChatToken,
		loadInitialConversations
	])

	const reInitializeChatClient = useCallback(
		async (userProfileId_: string) => {
			if (userProfileId_ && !chatClientInitialized) {
				try {
					const r = await getChatToken()
					if (r.data) {
						const client = new ConversationsClient(r.data, { logLevel: 'info' })
						setChatClient(client)
						setChatClientInitialized(true)

						client.on('initialized', async () => {
							await loadInitialConversations(client)
						})
					}
				} catch (error) {
					console.error('Failed to re-initialize chat client', error)
				}
			}
		},
		[getChatToken, chatClientInitialized, loadInitialConversations]
	)

	const contextValue = useMemo(
		() => ({
			chatClient,
			chatClientInitialized,
			conversations,
			unreadConversations,
			reInitializeChatClient,
			parseConversationDisplayName,
			parseConversationUniqueName,
			markConversationAccessed
		}),
		[
			chatClient,
			chatClientInitialized,
			conversations,
			unreadConversations,
			reInitializeChatClient,
			markConversationAccessed
		]
	)

	return (
		<ChatContext.Provider value={contextValue}>{children}</ChatContext.Provider>
	)
}

export default ChatProvider
